<template>
  <div class="p-2 rounded-lg border">
    <div class="flex items-center gap-[1rem] mb-2">
      <div
        class="inline-flex size-[35px] rounded-circle"
        :class="role.bgIcon"
      >
        <span
          class="text-[16px] m-auto"
          :class="role.icon"
        />
      </div>
      <span class="font-medium text-lg text-black">{{ role.title }}</span>
    </div>
    <div>
      <ValidationObserver #default="{ invalid }">
        <BForm
          class="flex flex-col gap-[1rem]"
          @submit.prevent="onSubmit"
        >
          <!-- Work Mode -->
          <ValidationProvider
            v-slot="{ errors }"
            rules="required"
            name="Setting Mode Kerja"
            vid="work_mode"
            class="w-full"
          >
            <label
              class="font-medium text-[14px] block mb-2"
            >
              Setting Mode Kerja:
            </label>
            <BTabs
              v-model="form.work_mode"
              class="rounded-pill bg-[#E2E2E2] p-[.25rem]"
              :class="{ 'is-invalid': errors.length > 0 }"
              pills
              active-nav-item-class="rounded-pill"
              nav-class="mb-0"
              @input="onChange"
            >
              <BTab
                v-for="(item, index) in workModeOptions"
                :key="index"
                :title="item"
                :active="index === form.work_mode"
                lazy
                title-item-class="bg-[#E2E2E2] rounded-pill w-1/2"
                @click="isTriggeredByUser = true"
              />
            </BTabs>
            <span
              v-if="errors.length > 0"
              class="text-danger text-sm"
            >{{ errors[0] }}</span>
          </ValidationProvider>
          <div class="grid grid-cols-2 items-start gap-[1rem]">
            <p
              class="font-medium text-[14px] col-span-2 m-0"
            >
              Setting Jam Kerja:
            </p>
            <ValidationProvider
              v-slot="{ errors }"
              rules="required|beforeTime:@end_clock"
              name="Kedatangan"
              vid="start_clock"
              class="w-full"
            >
              <label
                class="text-[14px] block mb-1"
              >
                Kedatangan
              </label>
              <TimePicker
                v-model="form.start_clock"
                :icon="errors.length > 0 ? '' : 'k-clock-1 text-[16px]'"
                placeholder="Pilih Kedatangan"
                :input-class="{
                  'is-invalid': errors.length > 0,
                }"
              />
              <span
                v-if="errors.length > 0"
                class="text-danger text-sm"
              >{{ errors[0] }}</span>
            </ValidationProvider>
            <ValidationProvider
              v-slot="{ errors }"
              rules="required|afterTime:@start_clock"
              name="Kepulangan"
              vid="end_clock"
              class="w-full"
            >
              <label
                class="text-[14px] block mb-1"
              >
                Kepulangan
              </label>
              <TimePicker
                v-model="form.end_clock"
                :icon="errors.length > 0 ? '' : 'k-clock-1 text-[16px]'"
                placeholder="Pilih Kedatangan"
                :input-class="{
                  'is-invalid': errors.length > 0,
                }"
              />
              <span
                v-if="errors.length > 0"
                class="text-danger text-sm"
              >{{ errors[0] }}</span>
            </ValidationProvider>
          </div>
          <BButton
            v-show="role.isShownSaveButton"
            type="submit"
            size="md"
            :variant="invalid ? 'secondary' : 'primary'"
            :disabled="invalid"
            class="m-0"
            full-width
          >
            Simpan Perubahan
          </BButton>
        </BForm>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import TimePicker from '@/@core/components/time-picker/TimePicker.vue'
import { afterTime, beforeTime, required } from '@/@core/utils/validations/validations'
import { komtimAxiosIns } from '@/libs/axios'
import { workModes } from './constants'

export default {
  components: {
    ValidationObserver,
    ValidationProvider,
    TimePicker,
  },
  props: {
    /**
     * Role to edit
     */
    role: {
      type: Object,
      default: () => ({
        id: null,
        title: '',
        icon: '',
        bgIcon: '',
        data: {},
        isShownSaveButton: false,
        triggerSource: '',
      }),
    },
  },
  data() {
    return {
      beforeTime,
      afterTime,
      required,
      workModeOptions: workModes,
      form: {
        work_mode: 0,
        start_clock: null,
        end_clock: null,
      },
      isTriggeredByUser: false,
    }
  },
  watch: {
    /**
     * Fill form with initial value
     */
    role: {
      immediate: true,
      handler({ data }) {
        if (!data) return

        this.form = {
          work_mode: this.workModeOptions.findIndex(mode => mode === data.work_mode),
          start_clock: data.start_clock,
          end_clock: data.end_clock,
        }
        this.isTriggeredByUser = false
      },
    },
  },
  methods: {
    /**
     * Handle tabs button change
     */
    onChange() {
      if (!this.isTriggeredByUser) return

      const { work_mode, start_clock, end_clock } = this.form
      const payload = {
        work_mode: this.workModeOptions[work_mode],
        start_clock,
        end_clock,
      }

      this.$emit('on-change', payload)
    },
    /**
     * Handle submit action
     */
    async onSubmit() {
      const url = `v1/work_configurations/${this.role.id}`
      try {
        const { work_mode, start_clock, end_clock } = this.form
        const syncOption = this.role.triggerSource === 'negative-btn' ? 'sync' : 'keep'
        const payload = {
          work_mode: this.workModeOptions[work_mode],
          check_in_time: start_clock,
          check_out_time: end_clock,
          sync_option: syncOption,
        }
        const { data: { data: { schedule_status } } } = await komtimAxiosIns.put(url, payload)
        this.$emit('on-after-submit', { status: 'success', schedule_status })
      } catch (error) {
        this.$emit('on-after-submit', { status: 'fail' })
      }
    },
    /**
     * Handle reset form
     */
    resetForm() {
      const { data: { work_mode: workMode, start_clock, end_clock } } = this.role
      this.form = {
        work_mode: this.workModeOptions.findIndex(item => item === workMode),
        start_clock,
        end_clock,
      }
      this.isTriggeredByUser = false
    },
  },
}
</script>
